/**
 * Phase 8 base AC15 — I/E checklist panel on Visit Detail.
 *
 * Renders the visit template's active criteria (typically at
 * Screening) with a Met / Not met toggle per row. Deviation text is
 * required when an inclusion criterion is marked "not met" or an
 * exclusion criterion is marked "met" — the FE mirrors the BE gate
 * so the coordinator sees the requirement inline.
 *
 * The panel hides itself when the visit's template has no criteria
 * configured — `status === 'not_configured'` from the BE.
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  AlertTriangle,
  CheckCircle2,
  ClipboardList,
  Loader2,
  Save,
  XCircle,
} from 'lucide-react';
import {
  ieChecklist,
  type IeCriterionRow,
  type IeResponseRow,
} from '@/api/client';
import { fmtDateTime } from '@/lib/dates';
import { errorText } from '@/lib/permissionError';

type Answer = { met: boolean; deviation_text: string };

function requiresDeviationText(
  criterionType: IeCriterionRow['criterion_type'],
  met: boolean,
): boolean {
  if (criterionType === 'inclusion') return met === false;
  return met === true; // exclusion
}

export function VisitIeChecklistPanel({ visitId }: { visitId: string }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['visit', 'ie-checklist', visitId],
    queryFn: () => ieChecklist.getForVisit(visitId),
    enabled: !!visitId,
  });

  const criteria = q.data?.criteria ?? [];
  const latest = useMemo(() => {
    const m = new Map<string, IeResponseRow>();
    for (const r of q.data?.responses_latest ?? []) m.set(r.criterion_id, r);
    return m;
  }, [q.data]);

  const [draft, setDraft] = useState<Map<string, Answer>>(new Map());
  const [dirty, setDirty] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!q.data) return;
    const next = new Map<string, Answer>();
    for (const c of q.data.criteria) {
      const r = latest.get(c.criterion_id);
      next.set(c.criterion_id, {
        met: r ? r.met === 'true' : true,
        deviation_text: r?.deviation_text ?? '',
      });
    }
    setDraft(next);
    setDirty(new Set());
  }, [q.data, latest]);

  const save = useMutation({
    mutationFn: () => {
      const responses = Array.from(dirty).map((cid) => {
        const a = draft.get(cid)!;
        return {
          criterion_id: cid,
          met: a.met,
          deviation_text: a.deviation_text.trim() || undefined,
        };
      });
      return ieChecklist.postResponses(visitId, { responses });
    },
    onSuccess: (r) => {
      toast.success(
        r.deviations_recorded > 0
          ? `Saved ${r.responses_recorded} response${r.responses_recorded === 1 ? '' : 's'} (${r.deviations_recorded} deviation${r.deviations_recorded === 1 ? '' : 's'}).`
          : `Saved ${r.responses_recorded} response${r.responses_recorded === 1 ? '' : 's'}.`,
      );
      setDirty(new Set());
      qc.invalidateQueries({ queryKey: ['visit', 'ie-checklist', visitId] });
      qc.invalidateQueries({ queryKey: ['visit', 'sign-history', visitId] });
    },
    onError: (e) => toast.error(errorText(e, 'Save failed')),
  });

  // Hide the panel entirely for visits whose template has no I/E
  // criteria configured — matches AC15's "when the visit is configured
  // to require I/E review" phrasing.
  if (q.data?.status === 'not_configured') return null;

  if (q.isLoading) {
    return (
      <section className="card p-4 text-xs text-ink-muted">
        <Loader2 className="mr-1 inline h-3 w-3 animate-spin" /> Loading I/E checklist…
      </section>
    );
  }

  const localViolations = new Map<string, string>();
  for (const [cid, a] of draft.entries()) {
    const c = criteria.find((x) => x.criterion_id === cid);
    if (!c) continue;
    if (requiresDeviationText(c.criterion_type, a.met) && !a.deviation_text.trim()) {
      localViolations.set(
        cid,
        `Deviation text required for this ${c.criterion_type} criterion.`,
      );
    }
  }

  const canSave = dirty.size > 0 && localViolations.size === 0 && !save.isPending;
  const completion = q.data?.completion;

  return (
    <section className="card p-4">
      <header className="mb-3 flex items-start justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-sm font-semibold text-ink-primary">
            <ClipboardList className="h-4 w-4 text-brand-500" />
            Inclusion / Exclusion checklist
          </h2>
          <p className="mt-0.5 text-[11px] text-ink-muted">
            Protocol-defined criteria reviewed at this visit. Deviations must be
            recorded but do not block Sign Visit; missing answers do.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {completion && (
            <span
              className={`inline-flex items-center gap-1 rounded border px-2 py-1 text-[10px] font-medium ${
                completion.is_complete
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                  : 'border-amber-200 bg-amber-50 text-amber-700'
              }`}
            >
              {completion.is_complete ? (
                <CheckCircle2 className="h-3 w-3" />
              ) : (
                <AlertTriangle className="h-3 w-3" />
              )}
              {completion.answered_criteria}/{completion.total_criteria} answered
            </span>
          )}
          <button
            type="button"
            onClick={() => save.mutate()}
            disabled={!canSave}
            className="rounded bg-accent-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-700 disabled:opacity-50"
          >
            <Save className="mr-1 inline h-3 w-3" />
            {save.isPending ? 'Saving…' : `Save ${dirty.size || ''}`}
          </button>
        </div>
      </header>

      {criteria.length === 0 ? (
        <p className="text-[11px] text-ink-muted">
          No active criteria configured on this visit template.
        </p>
      ) : (
        <ul className="space-y-2">
          {criteria.map((c) => {
            const a = draft.get(c.criterion_id) ?? { met: true, deviation_text: '' };
            const r = latest.get(c.criterion_id);
            const isDirty = dirty.has(c.criterion_id);
            const needsDeviation = requiresDeviationText(c.criterion_type, a.met);
            const violation = localViolations.get(c.criterion_id);
            return (
              <li
                key={c.criterion_id}
                className={`rounded border p-3 text-xs ${
                  violation
                    ? 'border-danger-300 bg-danger-50/30'
                    : isDirty
                      ? 'border-amber-200 bg-amber-50/30'
                      : 'border-surface-border bg-surface-card'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span
                        className={`rounded border px-1.5 py-0.5 text-[9px] font-semibold uppercase ${
                          c.criterion_type === 'inclusion'
                            ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                            : 'border-danger-200 bg-danger-50 text-danger-700'
                        }`}
                      >
                        {c.criterion_type}
                      </span>
                      <span className="font-mono text-[10px] text-ink-muted">
                        {c.reference_number}
                      </span>
                    </div>
                    <p className="mt-1 text-ink-primary">{c.criterion_text}</p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <button
                      type="button"
                      onClick={() => {
                        setDraft((prev) => {
                          const copy = new Map(prev);
                          copy.set(c.criterion_id, { ...a, met: true });
                          return copy;
                        });
                        setDirty((prev) => new Set(prev).add(c.criterion_id));
                      }}
                      className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 text-[11px] font-medium ${
                        a.met
                          ? 'border-emerald-400 bg-emerald-50 text-emerald-700'
                          : 'border-surface-border text-ink-muted hover:bg-surface-muted/40'
                      }`}
                    >
                      <CheckCircle2 className="h-3 w-3" /> Met
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setDraft((prev) => {
                          const copy = new Map(prev);
                          copy.set(c.criterion_id, { ...a, met: false });
                          return copy;
                        });
                        setDirty((prev) => new Set(prev).add(c.criterion_id));
                      }}
                      className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 text-[11px] font-medium ${
                        !a.met
                          ? 'border-danger-400 bg-danger-50 text-danger-700'
                          : 'border-surface-border text-ink-muted hover:bg-surface-muted/40'
                      }`}
                    >
                      <XCircle className="h-3 w-3" /> Not met
                    </button>
                  </div>
                </div>
                {needsDeviation && (
                  <label className="mt-2 block">
                    <span className="block text-[10px] font-semibold text-danger-700">
                      Deviation reason (required)
                    </span>
                    <textarea
                      value={a.deviation_text}
                      onChange={(e) => {
                        setDraft((prev) => {
                          const copy = new Map(prev);
                          copy.set(c.criterion_id, {
                            ...a,
                            deviation_text: e.target.value,
                          });
                          return copy;
                        });
                        setDirty((prev) => new Set(prev).add(c.criterion_id));
                      }}
                      rows={2}
                      placeholder="Describe why the criterion is not met and any remediation."
                      className="mt-1 w-full rounded border border-surface-border px-2 py-1 text-[11px]"
                    />
                  </label>
                )}
                {violation && (
                  <p className="mt-1 text-[10px] font-medium text-danger-700">
                    {violation}
                  </p>
                )}
                {r && !isDirty && (
                  <p className="mt-2 text-[10px] text-ink-muted">
                    Last recorded {fmtDateTime(r.recorded_at)}{' '}
                    <span className="font-mono">({r.reference_number})</span>
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
